import { type ReactNode, useState } from "react";
import { FormattedMessage } from "react-intl";
import classnames from "classnames";

import IconButton from "common/core/button/icon_button";
import SROnly from "common/core/screen_reader";

import Styles from "./index.module.scss";

type Children = {
  children: ReactNode;
};

type CardProps = {
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  fullWidth?: boolean;
  header?: ReactNode;
  collapsible?: boolean;
  expanded?: boolean;
  loading?: boolean;
  automationId?: string;
};

export function Card({
  children,
  footer,
  fullWidth,
  className,
  header,
  collapsible,
  expanded,
  loading,
  automationId,
}: CardProps) {
  const cx = classnames(Styles.card, { [Styles.fullWidth]: fullWidth }, className);
  const [isCollapsed, setIsCollapsed] = useState(!expanded);
  return (
    <section className={cx} data-automation-id={automationId || "card"}>
      {loading && (
        <>
          <SROnly>
            <FormattedMessage
              id="9513ebb8-9caa-491a-949d-8bea44731bfc"
              defaultMessage="Loading..."
            />
          </SROnly>
          <div className={Styles.loadingIndicator} aria-hidden="true" />
        </>
      )}
      {header && (
        <header className={Styles.header}>
          {header}
          {collapsible && (
            <IconButton
              variant="tertiary"
              buttonColor="dark"
              buttonSize="condensed"
              onClick={() => {
                setIsCollapsed(!isCollapsed);
              }}
              aria-expanded={!isCollapsed}
              automationId="card-collapse-button"
              name={isCollapsed ? "caret-down" : "caret-up"}
              className={Styles.icon}
              aria-label={isCollapsed ? "Expand content" : "Collapse content"}
            />
          )}
        </header>
      )}
      {(!collapsible || !isCollapsed) && (
        <>
          <div
            className={!collapsible ? Styles.container : Styles.expandedContainer}
            aria-hidden={!isCollapsed}
          >
            {children}
          </div>
          {footer && <footer className={Styles.footer}>{footer}</footer>}
        </>
      )}
    </section>
  );
}

export function CardHeading({ children }: Children) {
  return <h3 className={Styles.heading}>{children}</h3>;
}

// fix current usages of CardHeading then change CardHeadingLarge to be CardHeading
// https://notarize.atlassian.net/browse/ENT-3729
export function CardHeadingLarge({ children }: Children) {
  return <h3 className={Styles.headingLarge}>{children}</h3>;
}

export function CardSection({ children }: Children) {
  return <div className={Styles.section}>{children}</div>;
}

export function CardText({ children }: Children) {
  return <div className={Styles.text}>{children}</div>;
}
